/**
 * Objectives set in conversation (13/10/2026): in step ② of the qualification,
 * the analyst proposes the company's objectives from the qualification, explains
 * them in the chat and revises them when challenged. Its proposals wait here
 * until the manager validates or dismisses each one; validating writes
 * `workspace_objectives` under the manager's own rights.
 */

export const OBJECTIVE_PRIORITIES = ['critical', 'high', 'normal'] as const;
export type ObjectivePriority = typeof OBJECTIVE_PRIORITIES[number];

export const OBJECTIVE_PRIORITY_LABELS: Record<ObjectivePriority, string> = {
  critical: 'Critique',
  high: 'Importante',
  normal: 'Normale',
};

export const OBJECTIVE_PROPOSAL_ACTIONS = ['create', 'update', 'archive'] as const;
export type ObjectiveProposalAction = typeof OBJECTIVE_PROPOSAL_ACTIONS[number];

/** Limits of `workspace_objectives` (its check constraints). */
export const OBJECTIVE_LIMITS = { title: 180, measure: 1500, target: 200, baseline: 200 } as const;

export interface ObjectiveProposal {
  /** Stable while the proposal is revised; assigned by the code. */
  id: string;
  action: ObjectiveProposalAction;
  /** The objective it changes or archives; null for a new one. */
  objectiveId: string | null;
  title: string;
  /** What is counted, where it is read: the objective's success criteria. */
  measure: string;
  target: string | null;
  baseline: string | null;
  dueDate: string | null;
  priority: ObjectivePriority;
  /** A member the objective belongs to; null = the company's. */
  ownerUserId: string | null;
  /**
   * A lever: the main objective it produces, as a proposal id or an active
   * objective id; null for a main objective. One level only.
   */
  parentId: string | null;
  /** Why, from the qualification (one sentence). */
  why: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown, max: number): string => (typeof value === 'string' ? value.trim().slice(0, max) : '');
const optional = (value: unknown, max: number): string | null => text(value, max) || null;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Keeps the proposals that can be applied: an unknown objective or owner is
 * dropped (never redirected to another one), an update or archive needs its
 * objective, a creation or update needs a title and a measure. Ids are kept when
 * they match a previous proposal, otherwise assigned. A lever names its main
 * objective by the `ref` the model gave a new proposal, a proposal id or an
 * active objective id; a parent that is unknown, itself a lever, or the lever
 * itself makes it a main objective (never attached to another one).
 */
export const parseObjectiveProposals = (
  raw: unknown,
  known: {
    objectiveIds: ReadonlySet<string>;
    /** Active objectives that are levers: never a parent. */
    leverIds?: ReadonlySet<string>;
    memberIds: ReadonlySet<string>;
    previousIds?: ReadonlySet<string>;
  },
  newId: () => string = () => crypto.randomUUID(),
): ObjectiveProposal[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const refs = new Map<string, string>();
  const kept = raw.flatMap((entry): Array<{ proposal: ObjectiveProposal; parentRef: string | null }> => {
    if (!isRecord(entry)) return [];
    const action = OBJECTIVE_PROPOSAL_ACTIONS.find((candidate) => candidate === entry.action) ?? 'create';
    const objectiveId = typeof entry.objectiveId === 'string' && known.objectiveIds.has(entry.objectiveId) ? entry.objectiveId : null;
    if (action !== 'create' && !objectiveId) return [];
    const title = text(entry.title, OBJECTIVE_LIMITS.title);
    const measure = text(entry.measure, OBJECTIVE_LIMITS.measure);
    if (action !== 'archive' && (!title || !measure)) return [];
    const ownerUserId = typeof entry.ownerUserId === 'string' && known.memberIds.has(entry.ownerUserId) ? entry.ownerUserId : null;
    if (entry.ownerUserId && !ownerUserId) return [];
    const previous = typeof entry.id === 'string' && known.previousIds?.has(entry.id) && !seen.has(entry.id) ? entry.id : null;
    const id = previous ?? newId();
    seen.add(id);
    if (typeof entry.ref === 'string' && entry.ref.trim()) refs.set(entry.ref.trim(), id);
    return [{
      parentRef: typeof entry.parentRef === 'string' && entry.parentRef.trim() ? entry.parentRef.trim() : null,
      proposal: {
        id, action, objectiveId: action === 'create' ? null : objectiveId,
        title, measure,
        target: optional(entry.target, OBJECTIVE_LIMITS.target),
        baseline: optional(entry.baseline, OBJECTIVE_LIMITS.baseline),
        dueDate: typeof entry.dueDate === 'string' && DATE.test(entry.dueDate) ? entry.dueDate : null,
        priority: OBJECTIVE_PRIORITIES.find((candidate) => candidate === entry.priority) ?? 'normal',
        ownerUserId,
        parentId: null,
        why: text(entry.why, 400),
      },
    }];
  });
  const ids = new Set(kept.map(({ proposal }) => proposal.id));
  const resolve = (ref: string | null): string | null => {
    if (!ref) return null;
    if (refs.has(ref)) return refs.get(ref)!;
    if (ids.has(ref) || known.objectiveIds.has(ref)) return ref;
    return null;
  };
  const parents = new Map(kept.map(({ proposal, parentRef }) => [proposal.id, resolve(parentRef)]));
  return kept.map(({ proposal }) => {
    const parentId = parents.get(proposal.id) ?? null;
    const parentIsLever = parentId !== null && (Boolean(parents.get(parentId)) || Boolean(known.leverIds?.has(parentId)));
    const valid = parentId !== null && parentId !== proposal.id && parentId !== proposal.objectiveId && !parentIsLever && proposal.action !== 'archive';
    return { ...proposal, parentId: valid ? parentId : null };
  });
};
